import { useEffect } from "react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function PrivacyModal({ isOpen, onClose }: Props) {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-[640px] max-h-[85vh] flex flex-col mx-4 animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-achromatic-100">
          <h2 className="text-lg font-extrabold text-achromatic-900">개인정보 처리방침</h2>
          <button onClick={onClose} className="text-achromatic-400 hover:text-achromatic-600 transition-colors text-xl leading-none">✕</button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 text-[13px] text-achromatic-600 leading-[1.8]">
          <p className="text-xs text-achromatic-400 mb-4">시행일: 2026년 4월 1일</p>

          <p className="mb-4">
            주식회사 젤리펀치(이하 "회사")는 정보주체의 자유와 권리 보호를 위해 「개인정보 보호법」 및 관련 법령에 따라
            다음과 같이 개인정보 처리방침을 수립·공개합니다.
          </p>

          <h3 className="text-sm font-bold text-achromatic-900 mb-2">제1조 (수집하는 개인정보의 항목)</h3>
          <p className="mb-1 font-semibold text-achromatic-700">① 필수 수집 항목</p>
          <p className="mb-1 pl-3">• 카카오 소셜 로그인: 이메일</p>
          <p className="mb-4 pl-3">• 서비스 이용 기록: 예약 내역, 결제 내역, 접속 로그, IP 주소</p>

          <p className="mb-1 font-semibold text-achromatic-700">② 선택 수집 항목</p>
          <p className="mb-1 pl-3">• 카카오 로그인 시: 닉네임, 프로필 이미지, 전화번호 (선택 동의)</p>
          <p className="mb-4 pl-3">• 프로필 설정 시: 전화번호 (알림톡 수신 목적, 직접 입력)</p>

          <p className="mb-1 font-semibold text-achromatic-700">③ 결제 관련 항목</p>
          <p className="mb-4 pl-3">• 결제 수단 정보: PG사(토스페이먼츠)를 통해 처리되며, 회사는 카드번호 등을 직접 저장하지 않습니다.</p>

          <p className="mb-1 font-semibold text-achromatic-700">④ 정산 관련 항목 (해당 시)</p>
          <p className="mb-4 pl-3">• 출금 신청 시: 실명, 은행명, 계좌번호, 예금주</p>

          <h3 className="text-sm font-bold text-achromatic-900 mb-2">제2조 (개인정보의 수집 및 이용 목적)</h3>
          <p className="mb-1 pl-3">1. 회원 가입 및 관리: 회원 식별, 본인 인증, 서비스 부정 이용 방지</p>
          <p className="mb-1 pl-3">2. 서비스 제공: 예약 처리, 결제 승인·취소, 외부 접속 링크 전달</p>
          <p className="mb-1 pl-3">3. 알림 발송: 예약 확인, 결제 완료, 세션 리마인더 등 카카오 알림톡 발송</p>
          <p className="mb-1 pl-3">4. 정산: 호스트 수익 정산 처리</p>
          <p className="mb-1 pl-3">5. 고객 지원: 불만 처리, 공지사항 전달, 분쟁 조정</p>
          <p className="mb-4 pl-3">6. 서비스 개선: 통계 분석, 서비스 품질 개선</p>

          <h3 className="text-sm font-bold text-achromatic-900 mb-2">제3조 (개인정보의 보유 및 이용 기간)</h3>
          <p className="mb-1">회원 탈퇴 시 즉시 파기하며, 다음의 경우 관련 법령에 따라 보관합니다:</p>
          <p className="mb-1 pl-3">• 계약 또는 청약 철회 등에 관한 기록: 5년 (전자상거래법)</p>
          <p className="mb-1 pl-3">• 대금결제 및 재화 등의 공급에 관한 기록: 5년 (전자상거래법)</p>
          <p className="mb-1 pl-3">• 소비자의 불만 또는 분쟁 처리에 관한 기록: 3년 (전자상거래법)</p>
          <p className="mb-4 pl-3">• 접속에 관한 기록: 3개월 (통신비밀보호법)</p>

          <h3 className="text-sm font-bold text-achromatic-900 mb-2">제4조 (개인정보의 제3자 제공)</h3>
          <p className="mb-1">회사는 원칙적으로 정보주체의 동의 없이 개인정보를 제3자에게 제공하지 않습니다. 다만 다음의 경우에는 예외로 합니다:</p>
          <p className="mb-1 pl-3">• 정보주체가 사전에 동의한 경우</p>
          <p className="mb-1 pl-3">• 법률에 특별한 규정이 있거나 법적 의무 준수를 위한 경우</p>
          <p className="mb-4 pl-3">• 결제 처리를 위한 PG사(토스페이먼츠) 정보 전달</p>

          <h3 className="text-sm font-bold text-achromatic-900 mb-2">제5조 (개인정보 처리 위탁)</h3>
          <p className="mb-1">회사는 원활한 서비스 제공을 위해 다음과 같이 개인정보 처리 업무를 위탁합니다:</p>
          <p className="mb-1 pl-3">• 결제 처리: 토스페이먼츠 주식회사</p>
          <p className="mb-1 pl-3">• 알림톡 발송: 주식회사 솔라피 (Solapi)</p>
          <p className="mb-1 pl-3">• 클라우드 호스팅: Railway Inc. (미국)</p>
          <p className="mb-4 pl-3">• 정적 호스팅/CDN: Vercel Inc. (미국)</p>

          <h3 className="text-sm font-bold text-achromatic-900 mb-2">제5조의2 (개인정보의 국외 이전)</h3>
          <p className="mb-1">회사는 서비스 운영을 위해 다음과 같이 개인정보를 국외로 이전합니다:</p>
          <div className="bg-achromatic-50 rounded-xl p-4 mb-4">
            <p className="text-xs text-achromatic-600 leading-[1.8]">
              <strong>1. 서버 호스팅 (Railway Inc.)</strong><br />
              • 이전되는 항목: 회원 정보(이메일, 닉네임, 프로필, 전화번호), 서비스 이용 기록, 예약·결제 내역<br />
              • 이전 국가: 미국<br />
              • 이전 목적: 서비스 운영 및 데이터 저장·처리<br />
              • 보유 기간: 회원 탈퇴 시 또는 위탁 계약 종료 시까지<br />
              <br />
              <strong>2. 정적 호스팅 (Vercel Inc.)</strong><br />
              • 이전되는 항목: 접속 로그, IP 주소<br />
              • 이전 국가: 미국<br />
              • 이전 목적: 프론트엔드 애플리케이션 제공 및 CDN<br />
              • 보유 기간: 위탁 계약 종료 시까지<br />
              <br />
              <strong>3. 소셜 로그인 (카카오)</strong><br />
              • 이전되는 항목: 카카오 계정 식별 정보<br />
              • 이전 목적: 회원 인증<br />
              • 보유 기간: 회원 탈퇴 시까지
            </p>
          </div>
          <p className="mb-4 text-xs text-achromatic-500">
            ※ 회사는 「개인정보 보호법」 제28조의8에 따라 정보주체에게 국외 이전 사실을 고지하며,
            이전되는 국가의 개인정보 보호 수준, 보호 조치 등을 고려하여 안전한 관리가 이루어지도록 합니다.
          </p>

          <h3 className="text-sm font-bold text-achromatic-900 mb-2">제6조 (정보주체의 권리 및 행사 방법)</h3>
          <p className="mb-1">정보주체는 다음의 권리를 행사할 수 있습니다:</p>
          <p className="mb-1 pl-3">1. 개인정보 열람 요구</p>
          <p className="mb-1 pl-3">2. 오류 등이 있을 경우 정정 요구</p>
          <p className="mb-1 pl-3">3. 삭제 요구</p>
          <p className="mb-4 pl-3">4. 처리 정지 요구</p>
          <p className="mb-4">위 권리 행사는 서비스 내 설정 또는 help@jellypunch.com으로 요청할 수 있습니다.</p>

          <h3 className="text-sm font-bold text-achromatic-900 mb-2">제7조 (개인정보의 안전성 확보 조치)</h3>
          <p className="mb-1">회사는 개인정보의 안전성 확보를 위해 다음 조치를 시행합니다:</p>
          <p className="mb-1 pl-3">• 관리적 조치: 내부 관리 계획 수립·시행, 접근 권한 관리</p>
          <p className="mb-1 pl-3">• 기술적 조치: 개인정보 암호화, 보안 프로그램 설치, 접근 통제</p>
          <p className="mb-4 pl-3">• 물리적 조치: 서버실, 자료 보관실 등의 접근 통제</p>

          <h3 className="text-sm font-bold text-achromatic-900 mb-2">제8조 (개인정보 보호 책임자)</h3>
          <div className="bg-achromatic-50 rounded-xl p-4 mb-4">
            <p className="text-xs text-achromatic-600">
              <strong>개인정보 보호 책임자</strong><br />
              이름: 강해린 이사<br />
              소속: 주식회사 젤리펀치<br />
              이메일: help@jellypunch.com
            </p>
          </div>

          <h3 className="text-sm font-bold text-achromatic-900 mb-2">제9조 (권익 침해 구제 방법)</h3>
          <p className="mb-1">정보주체는 개인정보 침해로 인한 구제를 받기 위해 다음 기관에 분쟁 해결이나 상담을 신청할 수 있습니다:</p>
          <p className="mb-1 pl-3">• 개인정보 침해신고센터 (한국인터넷진흥원): 118</p>
          <p className="mb-1 pl-3">• 개인정보 분쟁조정위원회: 1833-6972</p>
          <p className="mb-4 pl-3">• 대검찰청 사이버수사과: 1301</p>

          <div className="bg-achromatic-50 rounded-xl p-4 mt-4">
            <p className="text-xs text-achromatic-500">
              <strong>주식회사 젤리펀치</strong><br />
              개인정보 보호 책임자: 강해린 이사<br />
              문의: help@jellypunch.com
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-achromatic-100">
          <button
            onClick={onClose}
            className="w-full py-3 bg-primary-500 text-white rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
}
